import { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerLayout from '../components/ManagerLayout';
import { Card } from '../components/ui/card';
import { Users, MapPin, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerDashboard = ({ manager, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    employees: 0,
    locations: 0,
    activeClocks: 0,
    pendingExceptions: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [employeesRes, locationsRes, timesheetsRes, exceptionsRes] =
        await Promise.all([
          axios.get(`${API}/employees`, { headers }),
          axios.get(`${API}/locations`),
          axios.get(`${API}/timesheets`, { headers }),
          axios.get(`${API}/exceptions?status=pending`, { headers }),
        ]);

      const activeEmployees = employeesRes.data.filter(
        (emp) => emp.status === 'active'
      ).length;
      const activeClocks = timesheetsRes.data.filter(
        (entry) => !entry.clock_out
      ).length;

      setStats({
        employees: activeEmployees,
        locations: locationsRes.data.length,
        activeClocks: activeClocks,
        pendingExceptions: exceptionsRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const statCards = [
    {
      title: 'Active Employees',
      value: stats.employees,
      icon: Users,
      color: 'bg-gray-900',
      testId: 'active-employees-stat',
    },
    {
      title: 'Locations',
      value: stats.locations,
      icon: MapPin,
      color: 'bg-gray-800',
      testId: 'locations-stat',
    },
    {
      title: 'Currently Clocked In',
      value: stats.activeClocks,
      icon: Clock,
      color: 'bg-gray-700',
      testId: 'clocked-in-stat',
    },
    {
      title: 'Pending Exceptions',
      value: stats.pendingExceptions,
      icon: AlertCircle,
      color: 'bg-orange-500',
      testId: 'pending-exceptions-stat',
    },
  ];

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {manager?.full_name}!</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card
            key={stat.title}
            className="p-6 hover:shadow-lg transition-all"
            data-testid={stat.testId}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/manager/employees')}
              className="w-full text-left p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:from-gray-100 hover:to-gray-200 transition-all border-l-4 border-gray-900"
              data-testid="quick-action-employees"
            >
              <h3 className="font-semibold text-gray-900">Manage Employees</h3>
              <p className="text-sm text-gray-700">Add or edit employee information</p>
            </button>
            <button
              onClick={() => navigate('/manager/timesheets')}
              className="w-full text-left p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:from-gray-100 hover:to-gray-200 transition-all border-l-4 border-gray-800"
              data-testid="quick-action-timesheets"
            >
              <h3 className="font-semibold text-gray-900">View Timesheets</h3>
              <p className="text-sm text-gray-700">
                Export data for QuickBooks
              </p>
            </button>
            <button
              onClick={() => navigate('/manager/exceptions')}
              className="w-full text-left p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg hover:from-orange-100 hover:to-orange-200 transition-all border-l-4 border-orange-600"
              data-testid="quick-action-exceptions"
            >
              <h3 className="font-semibold text-orange-900">
                Review Exceptions
              </h3>
              <p className="text-sm text-orange-700">
                {stats.pendingExceptions} pending approval
              </p>
            </button>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            System Info
          </h2>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex justify-between py-2 border-b">
              <span>Total Employees:</span>
              <span className="font-semibold text-gray-800">{stats.employees}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>Total Locations:</span>
              <span className="font-semibold text-gray-800">{stats.locations}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>Currently Clocked In:</span>
              <span className="font-semibold text-gray-800">
                {stats.activeClocks}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span>Pending Exceptions:</span>
              <span className="font-semibold text-gray-800">
                {stats.pendingExceptions}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </ManagerLayout>
  );
};

export default ManagerDashboard;